import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { RevealLines } from "@/components/RevealText";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getPost, BLOG_POSTS } from "@/data/blog";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ArrowUpRight } from "lucide-react";

const BlogPost = () => {
    const { slug } = useParams();
    const post = getPost(slug);
    if (!post) return <Navigate to="/blog" replace />;
    const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
    const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

    return (
        <div data-testid={`blog-post-${slug}`}>
            <section className="pt-40 pb-12 border-b border-white/10">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                    <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: post.title.slice(0, 30) + "…", to: `/blog/${slug}` }]} />
                    <div className="mt-8 flex items-center gap-4 text-[10px] tracking-widest uppercase text-slateink">
                        <span className="text-edge">{post.category}</span>
                        <span className="w-1 h-1 bg-slateink rounded-full" />
                        <span>{new Date(post.date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</span>
                        <span className="w-1 h-1 bg-slateink rounded-full" />
                        <span>{post.readTime}</span>
                    </div>
                    <RevealLines
                        as="h1"
                        lines={[post.title]}
                        className="mt-6 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.95]"
                    />
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="max-w-[1600px] mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative aspect-[16/8] overflow-hidden bg-ink-800"
                    >
                        <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 to-transparent" />
                    </motion.div>
                </div>
            </section>

            <article className="py-20 border-b border-white/10">
                <div className="max-w-[820px] mx-auto px-6 lg:px-12 space-y-8">
                    <p className="font-editorial italic text-xl md:text-2xl text-white/85 leading-relaxed">
                        {post.excerpt}
                    </p>
                    {post.body.map((block, i) => {
                        if (block.type === "h") return <h2 key={i} className="font-display uppercase text-2xl md:text-3xl font-black tracking-tight mt-12">{block.text}</h2>;
                        if (block.type === "p") return <p key={i} className="text-white/80 text-lg leading-relaxed">{block.text}</p>;
                        if (block.type === "ul") return (
                            <ul key={i} className="space-y-3">
                                {block.items.map((it, j) => (
                                    <li key={j} className="flex items-start gap-3 text-white/80">
                                        <span className="text-edge mt-2">✦</span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        );
                        return null;
                    })}
                </div>
            </article>

            <section className="py-20 border-b border-white/10">
                <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black tracking-tight">Talk to an insulation expert.</h2>
                        <p className="mt-4 text-white/70">Every project is different. Send us the basics and we'll call back.</p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-ink-900/50 p-8">
                        <EnquiryForm variant="compact" source={`blog-${slug}`} />
                    </div>
                </div>
            </section>

            <Link to={`/blog/${next.slug}`} className="block bg-ink-900 hover:bg-ink-800 py-16 group transition-colors">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase">Next Article</div>
                        <div className="font-display uppercase text-2xl md:text-4xl font-black tracking-tight mt-2 max-w-2xl">
                            {next.title}
                        </div>
                    </div>
                    <ArrowUpRight className="w-10 h-10 md:w-16 md:h-16 text-edge group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
            </Link>
        </div>
    );
};

export default BlogPost;
